import { ABP, ListService, PagedResultDto, TrackByService } from '@abp/ng.core';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { DateAdapter } from '@abp/ng.theme.shared/extensions';
import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import type {
  GetAssetsInput,
  AssetWithNavigationPropertiesDto,
} from '../../../proxy/assets/models';
import { AssetService } from '../../../proxy/assets/asset.service';
import { EquipmentDto } from '../../../proxy/equipments/models';
import { AssetStateTypeDto } from '../../../proxy/asset-state-types/models';
import { AssetStateTypeService } from '../../../proxy/asset-state-types/asset-state-type.service';
import { AssetEventsModalComponent } from '../../../asset-events/asset-event/components/asset-events-modal/asset-events-modal.component';
import { AssetWithNavigationPropertiesDtoEmpty } from '../../../proxy/assets/emptys-dto';
import { SearchAssetModalComponent } from './search-asset-modal/search-asset-modal.component';

@Component({
  selector: 'lib-asset',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [ListService, { provide: NgbDateAdapter, useClass: DateAdapter }],
  templateUrl: './asset.component.html',
  styleUrls: ['./asset.component.scss']
})
export class AssetComponent implements OnInit {
  data: PagedResultDto<AssetWithNavigationPropertiesDto> = {
    items: [],
    totalCount: 0,
  };

  filters = {} as GetAssetsInput;

  form: FormGroup;

  isFiltersHidden = true;

  isModalBusy = false;

  isModalOpen = false;

  selected?: AssetWithNavigationPropertiesDto;

  assetStateTypeDefaultOption: AssetStateTypeDto;

  equipment: EquipmentDto;
  @ViewChild(AssetEventsModalComponent) assetEventsModal: AssetEventsModalComponent;
  @ViewChild(SearchAssetModalComponent) searchAssetModal: SearchAssetModalComponent;

  constructor(
    public readonly list: ListService,
    public readonly track: TrackByService,
    public readonly service: AssetService,
    public readonly assetStateTypeService: AssetStateTypeService,
    private confirmation: ConfirmationService,
    private router: Router,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.loadAssets();
  }

  loadAssets(){
    const getData = (query: ABP.PageQueryParams) =>
      this.service.getList({
        ...query,
        ...this.filters,
        filterText: query.filter,
      });

    const setData = (list: PagedResultDto<AssetWithNavigationPropertiesDto>) => (this.data = list);

    this.list.hookToQuery(getData).subscribe(setData);
  }

  setEquipment(equipment: EquipmentDto){
    this.equipment = equipment;
    this.filters.equipmentId = this.equipment.id;
    this.loadAssets();
    this.searchAssetModal.setEquipment(equipment);
  }

  clearFilters() {
    this.filters = {} as GetAssetsInput;
  }

  buildForm() {
    const {
      name,
      description,
      model,
      brand,
      invoiceData,
      purshaseDate,
      garrantyExpirationDate,
      serialNumber,
      internalCode,
      assetTypeId,
      assetStateTypeId,
      providerId,
      equipmentId,
      locationId,
    } = this.selected?.asset || {assetStateTypeId: this.assetStateTypeDefaultOption?.id, equipmentId: this.equipment?.id};

    if(this.selected == null){
      this.selected = new AssetWithNavigationPropertiesDtoEmpty;
      this.selected.assetStateType = this.assetStateTypeDefaultOption;

      if(this.equipment != null)
        this.selected.equipment = this.equipment;
    }

    this.form = this.fb.group({
      name: [name ?? null, [Validators.required]],
      description: [description ?? null, []],
      model: [model ?? null, []],
      brand: [brand ?? null, []],
      invoiceData: [invoiceData ?? null, [Validators.required]],
      purshaseDate: [purshaseDate ? new Date(purshaseDate) : null, [Validators.required]],
      garrantyExpirationDate: [garrantyExpirationDate ? new Date(garrantyExpirationDate) : null, []],
      serialNumber: [serialNumber ?? null, []],
      internalCode: [internalCode ?? null, []],
      assetTypeId: [assetTypeId ?? null, []],
      assetStateTypeId: [assetStateTypeId ?? null, []],
      providerId: [providerId ?? null, []],
      equipmentId: [equipmentId ?? null, []],
      locationId: [locationId ?? null, []],
    });
  }

  hideForm() {
    this.isModalOpen = false;
    this.form.reset();
  }

  showForm() {
    if(this.assetStateTypeDefaultOption == null){
      this.assetStateTypeService.getDefaultOption().subscribe(response => {
        this.assetStateTypeDefaultOption = response;
        this.buildForm();
        this.isModalOpen = true;
      });
    }
    else{
      this.buildForm();
      this.isModalOpen = true;
    }
  }

  submitForm() {
    if (this.form.invalid) return;

    const request = this.selected?.asset?.id
      ? this.service.update(this.selected.asset.id, this.form.value)
      : this.service.create(this.form.value);

    this.isModalBusy = true;

    request
      .pipe(
        finalize(() => (this.isModalBusy = false)),
        tap(() => this.hideForm()),
      )
      .subscribe(this.list.get);
  }

  create() {
    this.selected = undefined;
    this.showForm();
  }

  update(record: AssetWithNavigationPropertiesDto) {
    this.selected = record;
    this.showForm();
  }

  delete(record: AssetWithNavigationPropertiesDto) {
    this.confirmation.warn(
      'Inventory::DeleteConfirmationMessage',
      'Inventory::AreYouSure',
      { messageLocalizationParams: [] }
    ).pipe(
      filter(status => status === Confirmation.Status.confirm),
      switchMap(() => this.service.delete(record.asset.id)),
    ).subscribe(this.list.get);
  }

  newEvent(record: AssetWithNavigationPropertiesDto) {
    this.assetEventsModal.create(record);
  }

  details(record: AssetWithNavigationPropertiesDto) {
    this.router.navigate(['inventory/assets', record.asset.id]);
  }

  showSearchAssetModal(){
    this.searchAssetModal.showForm();
  }
}
